import { Injectable, OnDestroy, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { io, ManagerOptions, Socket, SocketOptions } from 'socket.io-client';
import { PLATFORM_ID } from '@angular/core';
import { SOCKET_OPTIONS } from '@shared/utils/websocket.utils';
import { environment } from '@env/environment';
import { AuthStateService } from '@shared/services/auth-state.service';

export interface ISucursalSocket {
  sucursal_id: number;
  namespace: string;
  socket: Socket;
  isConnected: boolean;
}

/**
 * Servicio centralizado de gestion de sockets WebSocket.
 * Mejoras sobre SocketService original:
 * - Token refresh en reconexion (P15 fix)
 * - Limite de reconexion con reset (P16 fix)
 * - Limpieza automatica de listeners al desconectar (P14 fix)
 * - Escucha global de ws.error
 */
@Injectable({ providedIn: 'root' })
export class SocketService implements OnDestroy {
  private sockets$ = new BehaviorSubject<Map<string, ISucursalSocket>>(new Map());
  private connectionStatus$ = new BehaviorSubject<Map<string, boolean>>(new Map());
  private socketConfig = SOCKET_OPTIONS;
  private platformId = inject(PLATFORM_ID);
  private authState = inject(AuthStateService);

  /** Numero maximo de reintentos de reconexion por socket */
  private readonly MAX_RECONNECT_ATTEMPTS = 10;
  /** Tiempo de espera tras agotar reintentos (ms) */
  private readonly RECONNECT_RESET_DELAY = 60_000;
  /** Contador de reintentos por socket key */
  private reconnectAttempts = new Map<string, number>();

  constructor() {
    // La limpieza de sockets al cerrar sesion se maneja via:
    // 1. AuthStateService.clearSession() → auth guard redirige
    // 2. Componentes llaman stop() en ngOnDestroy
    // 3. El token viejo se rechaza en el backend automaticamente
  }

  ngOnDestroy(): void {
    this.desconectarTodos();
    this.sockets$.complete();
    this.connectionStatus$.complete();
  }

  private getKey(sucursal_id: number, namespace: string = ''): string {
    return `${sucursal_id}:${namespace}`;
  }

  setConfig(config: Partial<ManagerOptions & SocketOptions>): void {
    this.socketConfig = { ...this.socketConfig, ...config };
  }

  /**
   * Conecta a un socket para una sucursal especifica y namespace.
   * @param sucursal_id ID de la sucursal
   * @param namespace Namespace del socket (ej. '/mica-chat')
   * @param baseUrl URL base opcional (por defecto environment.apiUrl)
   */
  conectarSucursal(sucursal_id: number, namespace: string = '', baseUrl?: string): Socket | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    const socketsMap = this.sockets$.value;
    const key = this.getKey(sucursal_id, namespace);

    // Si ya existe una conexion, retornarla
    if (socketsMap.has(key)) {
      const existing = socketsMap.get(key)!;
      if (existing.isConnected) {
        return existing.socket;
      }
      existing.socket.connect();
      return existing.socket;
    }

    // Crear nueva conexion con el token actual
    const url = baseUrl ?? environment.apiUrl;
    const token = this.authState.accessToken();
    const socket = io(`${url}${namespace}`, {
      ...this.socketConfig,
      auth: {
        sucursal_id,
        ...(token ? { token } : {}),
      },
    });

    // Inicializar contador de reintentos
    this.reconnectAttempts.set(key, 0);

    this.configurarEventosSocket(socket, sucursal_id, namespace, key);

    const newSocket: ISucursalSocket = {
      sucursal_id,
      namespace,
      socket,
      isConnected: false,
    };

    socketsMap.set(key, newSocket);
    this.sockets$.next(socketsMap);

    return socket;
  }

  /** Desconecta un socket especifico */
  desconectarSocketEspecifico(sucursal_id: number, namespace: string = ''): void {
    const socketsMap = this.sockets$.value;
    const key = this.getKey(sucursal_id, namespace);
    const socketData = socketsMap.get(key);

    if (socketData) {
      socketData.socket.disconnect();
      socketsMap.delete(key);
      this.sockets$.next(socketsMap);

      const statusMap = this.connectionStatus$.value;
      statusMap.delete(key);
      this.connectionStatus$.next(statusMap);

      this.reconnectAttempts.delete(key);
    }
  }

  /** Desconecta todos los sockets de una sucursal */
  desconectarSucursal(sucursal_id: number): void {
    const socketsMap = this.sockets$.value;
    const keysToRemove: string[] = [];

    socketsMap.forEach((socketData, key) => {
      if (socketData.sucursal_id === sucursal_id) {
        socketData.socket.disconnect();
        keysToRemove.push(key);
      }
    });

    keysToRemove.forEach((key) => {
      socketsMap.delete(key);
      this.reconnectAttempts.delete(key);
    });
    this.sockets$.next(socketsMap);

    const statusMap = this.connectionStatus$.value;
    keysToRemove.forEach((key) => statusMap.delete(key));
    this.connectionStatus$.next(statusMap);
  }

  /** Desconecta todos los sockets */
  desconectarTodos(): void {
    const socketsMap = this.sockets$.value;
    socketsMap.forEach((socketData) => {
      socketData.socket.disconnect();
    });
    socketsMap.clear();
    this.sockets$.next(socketsMap);
    this.connectionStatus$.next(new Map());
    this.reconnectAttempts.clear();
  }

  obtenerSocket(sucursal_id: number, namespace: string = ''): Socket | null {
    const key = this.getKey(sucursal_id, namespace);
    return this.sockets$.value.get(key)?.socket || null;
  }

  obtenerTodosSockets(): Observable<Map<string, ISucursalSocket>> {
    return this.sockets$.asObservable();
  }

  obtenerEstadoConexiones(): Observable<Map<string, boolean>> {
    return this.connectionStatus$.asObservable();
  }

  estaConectado(sucursal_id: number, namespace: string = ''): boolean {
    const key = this.getKey(sucursal_id, namespace);
    return this.connectionStatus$.value.get(key) ?? false;
  }

  emitir(sucursal_id: number, evento: string, datos?: any, namespace: string = ''): void {
    const socket = this.obtenerSocket(sucursal_id, namespace);
    if (socket) {
      socket.emit(evento, datos);
    }
  }

  /** Escucha un evento de un socket. Limpia automaticamente al unsubscribe. */
  escuchar(sucursal_id: number, evento: string, namespace: string = ''): Observable<any> {
    return new Observable((observer) => {
      const socket = this.obtenerSocket(sucursal_id, namespace);
      if (!socket) {
        observer.error(
          new Error(`Socket no conectado para sucursal ${sucursal_id} namespace ${namespace}`),
        );
        return;
      }

      const handler = (datos: any) => observer.next(datos);
      socket.on(evento, handler);

      return () => {
        socket.off(evento, handler);
      };
    });
  }

  escucharUnaVez(sucursal_id: number, evento: string, namespace: string = ''): Promise<any> {
    return new Promise((resolve, reject) => {
      const socket = this.obtenerSocket(sucursal_id, namespace);
      if (socket) {
        socket.once(evento, (datos) => {
          resolve(datos);
        });
      } else {
        reject(
          new Error(`Socket no conectado para sucursal ${sucursal_id} namespace ${namespace}`),
        );
      }
    });
  }

  suscribirSucursal(sucursal_id: number, namespace: string = ''): void {
    const socket = this.obtenerSocketPorNamespace(namespace);
    if (!socket || !socket.connected) {
      console.warn(`[SocketService] No hay socket conectado para namespace ${namespace}`);
      return;
    }
    socket.emit('sucursal:subscribe', { sucursal_id });
  }

  desuscribirSucursal(sucursal_id: number, namespace: string = ''): void {
    const socket = this.obtenerSocketPorNamespace(namespace);
    if (!socket || !socket.connected) {
      console.warn(`[SocketService] No hay socket conectado para namespace ${namespace}`);
      return;
    }
    socket.emit('sucursal:unsubscribe', { sucursal_id });
  }

  private obtenerSocketPorNamespace(namespace: string): Socket | null {
    const socketsMap = this.sockets$.value;
    for (const [, socketData] of socketsMap) {
      if (socketData.namespace === namespace && socketData.isConnected) {
        return socketData.socket;
      }
    }
    return null;
  }

  private configurarEventosSocket(
    socket: Socket,
    sucursal_id: number,
    namespace: string,
    key: string,
  ): void {
    socket.on('connect', () => {
      this.actualizarEstadoConexion(key, sucursal_id, namespace, true);
      this.reconnectAttempts.set(key, 0);
    });

    socket.on('disconnect', () => {
      this.actualizarEstadoConexion(key, sucursal_id, namespace, false);
    });

    socket.on('connect_error', () => {
      this.actualizarEstadoConexion(key, sucursal_id, namespace, false);
    });

    // P15 fix: refrescar token en cada intento de reconexion
    socket.on('reconnect_attempt', () => {
      const attempts = (this.reconnectAttempts.get(key) ?? 0) + 1;
      this.reconnectAttempts.set(key, attempts);

      if (attempts > this.MAX_RECONNECT_ATTEMPTS) {
        socket.disconnect();
        setTimeout(() => {
          this.reconnectAttempts.set(key, 0);
          socket.connect();
        }, this.RECONNECT_RESET_DELAY);
        return;
      }

      // Actualizar el token por si cambio desde la ultima conexion
      const currentToken = this.authState.accessToken();
      if (currentToken) {
        (socket as any).auth = { ...(socket as any).auth, token: currentToken };
      }
    });

    // Escuchar errores del backend
    socket.on('ws.error', (error: { code: string; message: string }) => {
      console.warn(`[SocketService] Error WS (${namespace}):`, error);
    });
  }

  private actualizarEstadoConexion(
    key: string,
    sucursal_id: number,
    namespace: string,
    conectado: boolean,
  ): void {
    const socketsMap = this.sockets$.value;
    const socketData = socketsMap.get(key);

    if (socketData) {
      socketData.isConnected = conectado;
      socketsMap.set(key, socketData);
      this.sockets$.next(socketsMap);

      const statusMap = this.connectionStatus$.value;
      statusMap.set(key, conectado);
      this.connectionStatus$.next(statusMap);
    }
  }
}
